export interface TelemetryPoint {
  id?: number;
  client_point_id?: string;
  timestamp: string;
  latitude: number;
  longitude: number;
  altitude?: number;
  speed?: number;
  heart_rate?: number;
  cadence?: number;
}

export const toRad = (deg: number) => (deg * Math.PI) / 180;

export const haversineMeters = (a: TelemetryPoint, b: TelemetryPoint): number => {
  const R = 6371e3;
  const φ1 = toRad(a.latitude);
  const φ2 = toRad(b.latitude);
  const Δφ = toRad(b.latitude - a.latitude);
  const Δλ = toRad(b.longitude - a.longitude);
  const x = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * (Math.sin(Δλ / 2) ** 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  const d = R * c;
  return Number.isFinite(d) ? d : 0;
};

export const bearingDeg = (a: TelemetryPoint, b: TelemetryPoint): number => {
  const φ1 = toRad(a.latitude);
  const φ2 = toRad(b.latitude);
  const Δλ = toRad(b.longitude - a.longitude);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  const deg = (θ * 180) / Math.PI;
  return (deg + 360) % 360;
};

export const angleDiffDeg = (a: number, b: number): number => {
  let d = ((b - a + 540) % 360) - 180;
  if (!Number.isFinite(d)) d = 0;
  return d;
};

export const computeStressAtIndex = (
  points: TelemetryPoint[],
  idx: number,
): { stress: number; delta?: number } => {
  const p = points[idx];
  if (typeof p.heart_rate !== 'number' || p.heart_rate <= 0) return { stress: 1 };

  const window = points
    .slice(Math.max(0, idx - 10), idx)
    .map((x) => x.heart_rate)
    .filter((x): x is number => typeof x === 'number' && x > 0);

  if (window.length < 5) return { stress: 1 };
  const baseline = window.reduce((a, b) => a + b, 0) / window.length;
  const delta = p.heart_rate - baseline;
  const mag = Math.abs(delta);

  let stress = 1;
  if (mag >= 20) stress = 5;
  else if (mag >= 15) stress = 4;
  else if (mag >= 10) stress = 3;
  else if (mag >= 6) stress = 2;

  return { stress, delta: Math.round(delta) };
};

export const pointSortKey = (p: TelemetryPoint): number => {
  const t = Date.parse(p.timestamp);
  return Number.isFinite(t) ? t : 0;
};

export const pointDedupeKey = (p: TelemetryPoint): string => {
  if (typeof p.id === 'number') return `id:${p.id}`;
  if (p.client_point_id) return `cpid:${p.client_point_id}`;
  return `f:${p.timestamp}:${p.latitude}:${p.longitude}:${p.speed ?? ''}`;
};

export const mergeTelemetry = (
  existing: TelemetryPoint[],
  incoming: TelemetryPoint[],
): TelemetryPoint[] => {
  const byKey = new Map<string, TelemetryPoint>();
  for (const p of existing) byKey.set(pointDedupeKey(p), p);
  for (const p of incoming) byKey.set(pointDedupeKey(p), p);

  const merged = Array.from(byKey.values());
  merged.sort((a, b) => {
    const ta = pointSortKey(a);
    const tb = pointSortKey(b);
    if (ta !== tb) return ta - tb;
    const ia = a.id ?? 0;
    const ib = b.id ?? 0;
    return ia - ib;
  });
  return merged;
};
